import React, { useState } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18next';
import axios from 'axios';

import { addToast, handleAxiosError, useModals } from 'core/notifications';
import { Form } from 'core/forms';

import { Button, ButtonGroup } from 'uikit/buttons';

import getAdhocLettersSchema from '../schemas/getAdhocLettersSchema';

import getAxiosConfig from '../helpers/getAxiosConfig';

import useConfirmAction from '../hooks/useConfirmAction';
import useSendLetterAccess from '../hooks/useSendLetterAccess';

import styles from '../styles/generateAdhocLettersModal.module.scss';

const GenerateAdhocLettersModal = ({
  confirmBody = '',
  courseId = null,
  ids = [],
  idType = 'enrollment',
  isConfirmationRequired = false,
}) => {
  const [model, setModel] = useState({
    radioOptions: 'template',
  });

  const onUpdateModel = ({ update }) => {
    setModel({ ...model, ...update });
  };

  const { removeModal } = useModals();
  const { radioOptions } = model;
  const body = model.is_html ? model.html : model.body;
  const { hasCreateLetterAccess } = useSendLetterAccess();

  const getConfirmation = useConfirmAction({
    body: confirmBody,
    title: i18n.t('confirm-bulk-action'),
  });

  const isButtonDisabled =
    (radioOptions === 'custom' && (!model.subject || !body)) ||
    (radioOptions === 'template' && !model.letter_id);

  const onGenerateLetterClick = async ({ type }) => {
    if (isConfirmationRequired && !(await getConfirmation())) return;

    const templateOptionSpecificParams =
      radioOptions === 'custom'
        ? { subject: model.subject, body, is_html: model.is_html }
        : { letter_id: model.letter_id.id };
    const data = {
      ...templateOptionSpecificParams,
      course_id: courseId,
      [`${idType}_ids`]: ids,
      type,
    };
    try {
      await axios(getAxiosConfig('generate-adhoc-letter', { idType, ...data }));
      removeModal();
      addToast(i18n.t('letter-generated'), 'success');
    } catch (error) {
      handleAxiosError(error);
    }
  };

  return (
    <>
      <div className={styles.form}>
        <Form
          id="generate-adhoc-letters"
          model={model}
          onUpdate={onUpdateModel}
          schema={getAdhocLettersSchema({ hasCreateLetterAccess })}
        />
      </div>
      <ButtonGroup className={styles.buttons}>
        <Button
          isDisabled={isButtonDisabled}
          onClick={() => onGenerateLetterClick({ type: 'pdf' })}
          text={i18n.t('create-pdf')}
          variant="ghost"
        />
        <Button
          color="brand"
          isDisabled={isButtonDisabled}
          onClick={() => onGenerateLetterClick({ type: 'email' })}
          text={ids.length > 1 ? i18n.t('send-bulk-email') : i18n.t('send-email')}
        />
      </ButtonGroup>
    </>
  );
};

GenerateAdhocLettersModal.propTypes = {
  confirmBody: PropTypes.string,
  courseId: PropTypes.number,
  ids: PropTypes.arrayOf(PropTypes.number),
  idType: PropTypes.oneOf(['enrollment', 'user']),
  isConfirmationRequired: PropTypes.bool,
};

export default GenerateAdhocLettersModal;
